import React, { useEffect, useState } from 'react';
import { Button, Form, Input, Modal, Radio, Select, Spin, message } from 'antd';
import { getPlanFeatures, type PlanFeature } from '../permissions/permissionManageData';
import {
  createPlanFeatureGroup,
  featureNosFromDetail,
  updatePlanFeatureGroup,
  type FeatureGroupDetail
} from './permissionGroupManageData';

const GROUP_CODE_PATTERN = /^[A-Z][A-Z0-9_]{0,63}$/;
const GROUP_NO_PATTERN = /^[0-9]{21}$/;
const FEATURE_NO_PATTERN = /^[0-9]{21}$/;

type GroupFormValues = {
  groupCode?: string;
  displayName?: string;
  status?: string;
  featureNos?: string[];
};

type PermissionGroupFormModalProps = {
  open: boolean;
  mode: 'create' | 'edit';
  loading?: boolean;
  groupNo?: string;
  detail?: FeatureGroupDetail | null;
  configHash?: string;
  onClose: () => void;
  onCreated: () => void;
  onSaved: () => void;
};

function featureOptionLabel(item: PlanFeature) {
  const name = item.displayName?.trim() || item.featureCode?.trim() || item.featureNo?.trim() || '';
  const code = item.featureCode?.trim();
  return code && code !== name ? `${name} · ${code}` : name;
}

function validFeatureNos(values?: string[]) {
  return (values ?? []).map((item) => item.trim()).filter((item) => FEATURE_NO_PATTERN.test(item));
}

export function PermissionGroupFormModal({
  open,
  mode,
  loading,
  groupNo,
  detail,
  configHash,
  onClose,
  onCreated,
  onSaved
}: PermissionGroupFormModalProps) {
  const [createForm] = Form.useForm<GroupFormValues>();
  const [editForm] = Form.useForm<GroupFormValues>();
  const [features, setFeatures] = useState<PlanFeature[]>([]);
  const [featureLoading, setFeatureLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const editing = mode === 'edit';

  useEffect(() => {
    if (!open || editing) return;
    createForm.resetFields();
  }, [open, editing, createForm]);

  useEffect(() => {
    if (!open || !editing) return;
    const status = detail?.status === 'ACTIVE' || detail?.status === 'DISABLED' ? detail.status : undefined;
    editForm.setFieldsValue({
      groupCode: detail?.groupCode ?? '',
      displayName: detail?.displayName ?? '',
      status,
      featureNos: featureNosFromDetail(detail)
    });
  }, [open, editing, detail, editForm]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setFeatureLoading(true);
    const load = async () => {
      const collected: PlanFeature[] = [];
      for (let pageNum = 1; pageNum <= 10; pageNum += 1) {
        const res = await getPlanFeatures({ pageNum, pageSize: 100 });
        if (cancelled) return;
        const records = res.content?.records;
        if (res.code !== 200 || !Array.isArray(records)) {
          if (res.code !== -1) message.error(res.message || '权限点加载失败');
          setFeatures([]);
          return;
        }
        collected.push(
          ...records.filter(
            (item) => item.status === 'ACTIVE' && FEATURE_NO_PATTERN.test(item.featureNo?.trim() ?? '')
          )
        );
        if (records.length < 100) break;
      }
      if (!cancelled) setFeatures(collected);
    };
    load()
      .catch(() => {
        if (!cancelled) {
          message.error('权限点加载失败');
          setFeatures([]);
        }
      })
      .finally(() => {
        if (!cancelled) setFeatureLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const featureOptions = () => {
    const options = features.map((item) => ({
      value: item.featureNo?.trim(),
      label: featureOptionLabel(item)
    }));
    const known = new Set(options.map((item) => item.value));
    featureNosFromDetail(detail).forEach((id) => {
      if (!known.has(id)) {
        const feature = detail?.features?.find((item) => item.featureNo?.trim() === id);
        options.push({
          value: id,
          label: feature ? featureOptionLabel(feature) : id
        });
      }
    });
    return options;
  };

  const submitCreate = async () => {
    if (submitting) return;
    const values = await createForm.validateFields().catch(() => null);
    if (!values?.groupCode || !values.displayName) return;
    setSubmitting(true);
    try {
      const res = await createPlanFeatureGroup({
        groupCode: values.groupCode.trim(),
        displayName: values.displayName.trim(),
        featureNos: validFeatureNos(values.featureNos)
      });
      if (res.code !== 200) {
        if (res.code !== -1) message.error(res.message || '新增权限分组失败');
        return;
      }
      message.success(res.message || '已新增');
      onCreated();
    } catch {
      message.error('新增权限分组失败');
    } finally {
      setSubmitting(false);
    }
  };

  const submitEdit = async () => {
    if (submitting || loading) return;
    const values = await editForm.validateFields().catch(() => null);
    if (!values?.displayName) return;
    const targetGroupNo = (detail?.groupNo?.trim() || groupNo?.trim() || '');
    const displayName = values.displayName.trim();
    const status = values.status === 'ACTIVE' || values.status === 'DISABLED' ? values.status : '';
    if (!GROUP_NO_PATTERN.test(targetGroupNo)) {
      message.warning('功能组编码须为 21 位数字');
      return;
    }
    if (!displayName || displayName.length > 128 || !status) return;
    if (!configHash) {
      message.warning('缺少配置校验值，请关闭后重新打开');
      return;
    }
    setSubmitting(true);
    try {
      const res = await updatePlanFeatureGroup(targetGroupNo, {
        displayName,
        status,
        featureNos: validFeatureNos(values.featureNos),
        expectedConfigHash: configHash
      });
      if (res.code !== 200) {
        if (res.code !== -1) message.error(res.message || '保存权限分组失败');
        return;
      }
      message.success(res.message || '已保存');
      onSaved();
    } catch {
      message.error('保存权限分组失败');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={editing ? '编辑权限分组' : '新增权限分组'}
      open={open}
      onCancel={onClose}
      destroyOnClose
      centered
      width={560}
      footer={
        <div className='flex justify-end gap-2'>
          <Button className='rounded-lg' onClick={onClose}>
            取消
          </Button>
          <Button
            type='primary'
            className='wb-btn-primary rounded-lg'
            loading={submitting}
            disabled={loading}
            onClick={editing ? submitEdit : submitCreate}
          >
            {editing ? '保存' : '确认'}
          </Button>
        </div>
      }
    >
      {editing ? (
        <Spin spinning={loading}>
          <Form
            form={editForm}
            layout='horizontal'
            labelCol={{ flex: '110px' }}
            wrapperCol={{ flex: 1 }}
            labelAlign='right'
            colon
            requiredMark={(labelNode, { required }) => (
              <>
                {labelNode}
                {required ? <span className='admin-perm-required'>*</span> : null}
              </>
            )}
            className='pt-2 admin-perm-form'
          >
            <Form.Item label='功能组编码'>
              <Input disabled value={detail?.groupNo || groupNo || ''} />
            </Form.Item>
            <Form.Item name='groupCode' label='分组标识'>
              <Input disabled />
            </Form.Item>
            <Form.Item
              name='displayName'
              label='展示名称'
              rules={[
                { required: true, whitespace: true, message: '请输入展示名称' },
                { max: 128, message: '最多 128 字' }
              ]}
            >
              <Input placeholder='请输入展示名称' maxLength={128} allowClear />
            </Form.Item>
            <Form.Item
              name='status'
              label='状态'
              className='admin-perm-radio-item'
              rules={[{ required: true, message: '请选择状态' }]}
            >
              <Radio.Group
                options={[
                  { value: 'ACTIVE', label: '启用' },
                  { value: 'DISABLED', label: '停用' }
                ]}
              />
            </Form.Item>
            <Form.Item name='featureNos' label='权限点'>
              <Select
                mode='multiple'
                allowClear
                showSearch
                loading={featureLoading}
                placeholder='选择已启用的权限点，可不选'
                optionFilterProp='label'
                options={featureOptions()}
                notFoundContent={featureLoading ? '加载中' : '暂无已启用权限点'}
              />
            </Form.Item>
          </Form>
        </Spin>
      ) : (
        <Form
          form={createForm}
          layout='horizontal'
          labelCol={{ flex: '110px' }}
          wrapperCol={{ flex: 1 }}
          labelAlign='right'
          colon
          requiredMark={(labelNode, { required }) => (
            <>
              {labelNode}
              {required ? <span className='admin-perm-required'>*</span> : null}
            </>
          )}
          className='pt-2 admin-perm-form'
        >
          <Form.Item
            name='groupCode'
            label='分组标识'
            getValueFromEvent={(event) => String(event.target.value || '').toUpperCase()}
            rules={[
              { required: true, message: '请输入分组标识' },
              {
                pattern: GROUP_CODE_PATTERN,
                message: '大写字母开头，仅含大写字母、数字或下划线，1-64 位'
              }
            ]}
          >
            <Input placeholder='例如 EMAIL' maxLength={64} allowClear />
          </Form.Item>
          <Form.Item
            name='displayName'
            label='展示名称'
            rules={[
              { required: true, whitespace: true, message: '请输入展示名称' },
              { max: 128, message: '最多 128 字' }
            ]}
          >
            <Input placeholder='请输入展示名称' maxLength={128} allowClear />
          </Form.Item>
          <Form.Item name='featureNos' label='权限点'>
            <Select
              mode='multiple'
              allowClear
              showSearch
              loading={featureLoading}
              placeholder='选择已启用的权限点，可不选'
              optionFilterProp='label'
              options={featureOptions()}
              notFoundContent={featureLoading ? '加载中' : '暂无已启用权限点'}
            />
          </Form.Item>
        </Form>
      )}
    </Modal>
  );
}
